/**
 * `<countdown-bar>` — a live countdown to a fixed target date/time, rendered as
 * zero-padded days/hours/minutes/seconds. Two markup variants share this one element:
 * "simple" (inline `00:00:00:00` inside the header announcement bar) and "full"
 * (large stacked value+label items as a standalone theme block).
 *
 * The JS contract is purely the data attributes: value cells are addressed via
 * `[data-countdown-bar-el="days|hours|minutes|seconds"]` (querySelectorAll, since the
 * variants differ in structure), and an optional `[data-countdown-bar-el="daysitem"]`
 * wrapper is hidden (aria-hidden) while days is 0 so sub-24h countdowns collapse to
 * hours:minutes:seconds. The target is `new Date(`${date}T${time}:00`)` — deliberately
 * visitor-local time, matching the legacy theme. All state is instance-local: the
 * announcement bar marquee may clone its content, so several copies of the same
 * countdown coexist and must each tick independently.
 */
class CountdownBar extends HTMLElement {
  /** @type {Date | null} */
  #target = null;

  /** @type {number | undefined} */
  #intervalId;

  #expired = false;

  connectedCallback() {
    const date = this.getAttribute('data-date');
    const time = this.getAttribute('data-time') || '00:00';

    // Missing date or an unparseable target: bail without ever setting
    // data-initialised, so the CSS keeps the element hidden.
    if (!date) return;

    const target = new Date(`${date}T${time}:00`);
    if (Number.isNaN(target.getTime())) return;

    this.#target = target;

    this.#tick();

    // An already-expired target renders its zeros once — no interval needed.
    if (!this.#expired) {
      this.#intervalId = window.setInterval(() => this.#tick(), 1000);
    }
  }

  disconnectedCallback() {
    if (this.#intervalId !== undefined) clearInterval(this.#intervalId);
  }

  /**
   * Recomputes the remaining time and writes the zero-padded parts into every
   * value cell. On the first tick it sets `data-initialised="true"` (CSS reveals
   * the element); when the target is reached it renders zeros, stops the interval,
   * marks `data-expired="true"`, and dispatches a bubbling `countdown-bar:expired`.
   */
  #tick() {
    if (!this.#target) return;

    const remaining = Math.max(this.#target.getTime() - Date.now(), 0);

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.#write('days', days);
    this.#write('hours', hours);
    this.#write('minutes', minutes);
    this.#write('seconds', seconds);

    // Legacy CSS hides [aria-hidden=true] items, so a sub-24h countdown shows
    // hours:minutes:seconds only.
    const daysItem = this.querySelector('[data-countdown-bar-el="daysitem"]');
    if (daysItem) {
      if (days === 0) daysItem.setAttribute('aria-hidden', 'true');
      else daysItem.removeAttribute('aria-hidden');
    }

    if (this.getAttribute('data-initialised') !== 'true') {
      this.setAttribute('data-initialised', 'true');
    }

    if (remaining === 0 && !this.#expired) {
      this.#expired = true;
      if (this.#intervalId !== undefined) clearInterval(this.#intervalId);
      this.setAttribute('data-expired', 'true');
      this.dispatchEvent(new CustomEvent('countdown-bar:expired', { bubbles: true }));
    }
  }

  /**
   * Writes a zero-padded value into every descendant cell for a given unit.
   * @param {string} unit — one of `days|hours|minutes|seconds`.
   * @param {number} value
   */
  #write(unit, value) {
    const text = String(value).padStart(2, '0');
    for (const cell of this.querySelectorAll(`[data-countdown-bar-el="${unit}"]`)) {
      cell.textContent = text;
    }
  }
}

if (!customElements.get('countdown-bar')) {
  customElements.define('countdown-bar', CountdownBar);
}
